import React, {useEffect, useState} from 'react';
import {Button, Col, Form, Modal, Row} from '@themesberg/react-bootstrap';
import {FormattedMessage} from 'react-intl';
import {useHistory} from 'react-router-dom';
import API from '../services/API';
import Utils from '../services/Utils';
import Countries from './Countries';
import LANGUAGES from '../services/languages';
import {usePlan} from '../services/PlanContext';

const EMPTY = {
  firstName: '', lastName: '', companyName: '', email: '', phone: '', country: '',
  address: '', addressComplement: '', postalCode: '', city: '', language: '', notes: '',
};

// Création (customer absent) ou modification d'une fiche client depuis la page Clients.
// Monté uniquement pendant l'affichage : l'état du formulaire repart de la fiche à chaque ouverture.
export default ({intl, show, customer, onClose, onSaved}) => {
  const history = useHistory();
  const {openSubscription} = usePlan();
  const editing = Boolean(customer && customer.id);
  const [formData, setFormData] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!show) return;
    const initial = {...EMPTY};
    if (editing) {
      Object.keys(EMPTY).forEach(key => { initial[key] = customer[key] ?? ''; });
    } else {
      initial.language = Utils.getLanguage();
    }
    // Même défaut que la réservation manuelle (la liste des pays n'a pas d'option vide).
    if (!initial.country) initial.country = intl.locale === 'fr' ? 'FR' : 'US';
    if (!initial.language) initial.language = 'fr';
    setFormData(initial);
  }, [show]);

  const handleChange = (event) => {
    const {id, value} = event.target;
    setFormData(data => ({...data, [id]: value}));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const missing = [];
    if (!formData.firstName.trim()) missing.push(intl.formatMessage({id: 'FIRST_NAME'}));
    if (!formData.lastName.trim()) missing.push(intl.formatMessage({id: 'LAST_NAME'}));
    if (missing.length) {
      Utils.notifyWarning(intl.formatMessage({id: 'WARNING'}),
        intl.formatMessage({id: 'MISSING_REQUIRED_FIELDS'}, {fields: missing.join(', ')}));
      return;
    }

    setSaving(true);
    const request = editing ? API.putCustomer(history, customer.id, formData) : API.postCustomer(history, formData);
    request.then(response => {
      setSaving(false);
      if (response && response.data.success === true) {
        Utils.notifySuccess(intl.formatMessage({id: 'OK'}),
          intl.formatMessage({id: editing ? 'SAVED_CHANGES' : 'CUSTOMER_ADDED'}));
        onSaved(response.data.data);
      } else if (response && response.data.data === 'CUSTOMER_ALREADY_EXISTS') {
        Utils.notifyWarning(intl.formatMessage({id: 'WARNING'}), intl.formatMessage({id: 'CUSTOMER_ALREADY_EXISTS'}));
      } else if (response && response.data.data === 'BOOKING_LOCKED') {
        onClose();
        openSubscription();
      } else {
        Utils.notifyError(intl.formatMessage({id: 'ERROR'}), intl.formatMessage({id: 'ERROR_OCCURRED'}));
      }
    });
  };

  return (
    <Modal as={Modal.Dialog} centered show={show} onHide={onClose}>
      <Modal.Header>
        <Modal.Title className="h6">
          <FormattedMessage id={editing ? 'EDIT_CUSTOMER' : 'NEW_CUSTOMER'}/>
        </Modal.Title>
        <Button variant="close" aria-label="Close" onClick={onClose}/>
      </Modal.Header>
      <Modal.Body>
        <Form id="customer-form" onSubmit={handleSubmit}>
          <Row>
            <Col md={6} className="mb-3">
              <Form.Group>
                <Form.Label><FormattedMessage id="FIRST_NAME"/> *</Form.Label>
                <Form.Control id="firstName" type="text" required autoFocus value={formData.firstName} onChange={handleChange}/>
              </Form.Group>
            </Col>
            <Col md={6} className="mb-3">
              <Form.Group>
                <Form.Label><FormattedMessage id="LAST_NAME"/> *</Form.Label>
                <Form.Control id="lastName" type="text" required value={formData.lastName} onChange={handleChange}/>
              </Form.Group>
            </Col>
          </Row>
          <Form.Group className="mb-3">
            <Form.Label><FormattedMessage id="COMPANY_NAME"/></Form.Label>
            <Form.Control id="companyName" type="text" value={formData.companyName} onChange={handleChange}/>
          </Form.Group>
          <Row>
            <Col md={6} className="mb-3">
              <Form.Group>
                <Form.Label><FormattedMessage id="EMAIL_ADDRESS"/></Form.Label>
                <Form.Control id="email" type="email" value={formData.email} onChange={handleChange}/>
              </Form.Group>
            </Col>
            <Col md={6} className="mb-3">
              <Form.Group>
                <Form.Label><FormattedMessage id="PHONE"/></Form.Label>
                <Form.Control id="phone" type="tel" value={formData.phone} onChange={handleChange}/>
              </Form.Group>
            </Col>
          </Row>
          <Form.Group className="mb-3">
            <Form.Label><FormattedMessage id="STREET_ADDRESS"/></Form.Label>
            <Form.Control id="address" type="text" value={formData.address} onChange={handleChange}/>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label><FormattedMessage id="APARTMENT_OFFICE_ETC"/></Form.Label>
            <Form.Control id="addressComplement" type="text" value={formData.addressComplement} onChange={handleChange}/>
          </Form.Group>
          <Row>
            <Col md={4} className="mb-3">
              <Form.Group>
                <Form.Label><FormattedMessage id="ZIP_CODE"/></Form.Label>
                <Form.Control id="postalCode" type="text" maxLength="10" value={formData.postalCode} onChange={handleChange}/>
              </Form.Group>
            </Col>
            <Col md={8} className="mb-3">
              <Form.Group>
                <Form.Label><FormattedMessage id="CITY"/></Form.Label>
                <Form.Control id="city" type="text" value={formData.city} onChange={handleChange}/>
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={6} className="mb-3">
              <Form.Group>
                <Form.Label><FormattedMessage id="COUNTRY"/></Form.Label>
                {/* Pays saisi en texte libre par un ancien module de réservation : on le laisse modifiable tel quel. */}
                {/^[A-Z]{2}$/.test(formData.country)
                  ? <Countries id="country" intl={intl} value={formData.country} onChange={handleChange}/>
                  : <Form.Control id="country" type="text" value={formData.country} onChange={handleChange}/>}
              </Form.Group>
            </Col>
            <Col md={6} className="mb-3">
              <Form.Group>
                <Form.Label><FormattedMessage id="CUSTOMER_LANGUAGE"/></Form.Label>
                <Form.Select id="language" value={formData.language} onChange={handleChange}>
                  {LANGUAGES.map(l => <option key={l.code} value={l.code}>{l.flag} {l.label}</option>)}
                </Form.Select>
                <Form.Text className="text-muted"><FormattedMessage id="CUSTOMER_LANGUAGE_HINT"/></Form.Text>
              </Form.Group>
            </Col>
          </Row>
          <Form.Group>
            <Form.Label><FormattedMessage id="NOTES"/></Form.Label>
            <Form.Control id="notes" as="textarea" rows={3} value={formData.notes} onChange={handleChange}
                          placeholder={intl.formatMessage({id: 'CUSTOMER_NOTES_PLACEHOLDER'})}/>
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="success" type="submit" form="customer-form" disabled={saving}>
          <FormattedMessage id={editing ? 'RECORD' : 'ADD'}/>
        </Button>
        <Button variant="link" className="text-gray ms-auto" onClick={onClose}>
          <FormattedMessage id="CLOSE"/>
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
